import { useCallback, useLayoutEffect } from 'react'

import { SETTINGS_MODAL_RAIL_LAYOUT_CHANGE_EVENT } from 'shared/constants/app_shell'

const DEFAULT_GAP = 8
const VIEWPORT_PADDING = 8
const COMFORTABLE_MENU_WIDTH = 450
const DEFAULT_MENU_WIDTH = 414

const hasHorizontalPlacementHint = (placement) => {
  if (!placement) return false

  const normalized = placement.replace(/\s+/g, ' ').trim()
  return normalized.includes('start')
    || normalized.includes('end')
    || normalized.endsWith('left')
    || normalized.endsWith('right')
}

const parseHorizontalPlacement = (placement) => {
  const normalized = placement.replace(/\s+/g, ' ').trim()
  if (normalized.includes('end') || normalized.endsWith('right')) {
    return 'bottom-end'
  }

  return 'bottom-start'
}

export const getEstimatedMenuWidth = (menuEl, contentMinWidth) => {
  if (menuEl?.offsetWidth > 0) return menuEl.offsetWidth
  if (contentMinWidth === 'comfortable') return COMFORTABLE_MENU_WIDTH
  if (contentMinWidth === 'content') return 0
  return DEFAULT_MENU_WIDTH
}

export const resolvePortaledMenuPlacement = ({
  dataPlacement,
  contextPlacement = 'bottom start',
  pullLeft = false,
  anchorRect,
  menuWidth = 0,
  bounds,
}) => {
  if (pullLeft) return 'bottom-end'

  const placementSource = hasHorizontalPlacementHint(dataPlacement)
    ? dataPlacement
    : contextPlacement
  const horizontal = parseHorizontalPlacement(placementSource)

  if (horizontal === 'bottom-end') return 'bottom-end'

  if (menuWidth > 0 && anchorRect && bounds
    && anchorRect.left + menuWidth > bounds.right) {
    return 'bottom-end'
  }

  return 'bottom-start'
}

const SCROLL_OVERFLOW_VALUES = new Set(['auto', 'scroll', 'overlay'])

const getOverflowScrollParent = (anchorEl) => {
  if (!anchorEl) return null

  let node = anchorEl.parentElement
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node)
    if (SCROLL_OVERFLOW_VALUES.has(overflowY)) {
      return node
    }
    node = node.parentElement
  }

  return null
}

const getViewportBounds = () => ({
  top: VIEWPORT_PADDING,
  bottom: window.innerHeight - VIEWPORT_PADDING,
  left: VIEWPORT_PADDING,
  right: window.innerWidth - VIEWPORT_PADDING,
})

export const getPortaledMenuCollisionBounds = (anchorEl) => {
  const viewportBounds = getViewportBounds()

  const scrollParent = getOverflowScrollParent(anchorEl)
  if (!scrollParent) return viewportBounds

  const scrollRect = scrollParent.getBoundingClientRect()

  return {
    top: Math.max(viewportBounds.top, scrollRect.top),
    bottom: Math.min(viewportBounds.bottom, scrollRect.bottom),
    left: Math.max(viewportBounds.left, scrollRect.left),
    right: Math.min(viewportBounds.right, scrollRect.right),
  }
}

const shouldFlipMenuAbove = (anchorRect, menuHeight, bounds, gap = DEFAULT_GAP) => {
  if (!menuHeight) return false

  const belowTop = anchorRect.bottom + gap
  const fitsBelow = belowTop + menuHeight <= bounds.bottom

  if (fitsBelow) return false

  const aboveTop = anchorRect.top - menuHeight - gap
  return aboveTop >= bounds.top
}

const clampMenuTop = (top, menuHeight, bounds) => {
  if (!menuHeight) return top

  return Math.max(bounds.top, Math.min(top, bounds.bottom - menuHeight))
}

/*
 * Returns a menu's position plus the room available on the side it opens toward.
 *
 * `keepAnchoredBelow` pins the top to the anchor's bottom instead of sliding the menu up to fit.
 * A menu drawn as a tab attached to its trigger cannot be nudged upward: it would ride over the
 * trigger's bottom border and tear the seam (SA-4623). Those callers shrink to `maxHeight` instead.
 */
export const computePortaledMenuPosition = ({
  anchorRect,
  menuWidth,
  menuHeight,
  bounds,
  placement = 'bottom-end',
  gap = DEFAULT_GAP,
  keepAnchoredBelow = false,
}) => {
  const belowTop = anchorRect.bottom + gap
  let top = belowTop
  let left = placement === 'bottom-start'
    ? anchorRect.left
    : anchorRect.right - menuWidth

  left = Math.max(bounds.left, Math.min(left, bounds.right - menuWidth))

  const opensAbove = shouldFlipMenuAbove(anchorRect, menuHeight, bounds, gap)
  if (opensAbove) {
    top = anchorRect.top - menuHeight - gap
  }

  top = keepAnchoredBelow && !opensAbove ? belowTop : clampMenuTop(top, menuHeight, bounds)

  const maxHeight = opensAbove
    ? Math.max(0, anchorRect.top - gap - bounds.top)
    : Math.max(0, bounds.bottom - top)

  return { top, left, maxHeight }
}

const getMeasuredMenuSize = (menuEl, defaultMenuWidth) => {
  if (!menuEl) {
    return { width: defaultMenuWidth, height: 0 }
  }

  const rect = menuEl.getBoundingClientRect?.()
  const width = menuEl.offsetWidth || rect?.width || defaultMenuWidth
  const height = menuEl.offsetHeight || rect?.height || 0

  return {
    width: width > 0 ? width : defaultMenuWidth,
    height: height > 0 ? height : 0,
  }
}

/**
 * Keeps a portaled dropdown menu anchored to its trigger while escaping shell overflow/z-index stacking.
 */
export const usePortaledMenuPosition = (
  isOpen,
  anchorRef,
  menuRef,
  { placement = 'bottom-end', defaultMenuWidth = 414, useViewportBounds = false, gap = DEFAULT_GAP } = {}
) => {
  const updatePosition = useCallback(() => {
    if (!anchorRef.current) return

    const anchorRect = anchorRef.current.getBoundingClientRect()
    const { width: menuWidth, height: menuHeight } = getMeasuredMenuSize(
      menuRef.current,
      defaultMenuWidth,
    )
    const bounds = useViewportBounds
      ? getViewportBounds()
      : getPortaledMenuCollisionBounds(anchorRef.current)
    const { top, left } = computePortaledMenuPosition({
      anchorRect,
      menuWidth,
      menuHeight,
      bounds,
      placement,
      gap,
    })

    if (menuRef.current) {
      menuRef.current.style.top = `${top}px`
      menuRef.current.style.left = `${left}px`
      menuRef.current.style.removeProperty('right')
    }
  }, [anchorRef, menuRef, placement, defaultMenuWidth, useViewportBounds, gap])

  useLayoutEffect(() => {
    if (!isOpen) return undefined

    updatePosition()
    const raf = requestAnimationFrame(updatePosition)

    let resizeObserver
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updatePosition)
      if (menuRef.current) {
        resizeObserver.observe(menuRef.current)
      }
      if (anchorRef.current) {
        resizeObserver.observe(anchorRef.current)
      }
    }

    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener(SETTINGS_MODAL_RAIL_LAYOUT_CHANGE_EVENT, updatePosition)

    return () => {
      cancelAnimationFrame(raf)
      resizeObserver?.disconnect()
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener(SETTINGS_MODAL_RAIL_LAYOUT_CHANGE_EVENT, updatePosition)
    }
  }, [anchorRef, isOpen, menuRef, updatePosition])
}
